import _createForOfIteratorHelper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module utils/dom/resizeobserver
 */
import global from './global.js';
/**
 * A helper class which instances allow performing custom actions when native DOM elements are resized.
 *
 * ```ts
 * const editableElement = editor.editing.view.getDomRoot();
 *
 * const observer = new ResizeObserver( editableElement, entry => {
 * 	console.log( 'The editable element has been resized in DOM.' );
 * 	console.log( entry.target ); // -> editableElement
 * 	console.log( entry.contentRect.width ); // -> e.g. '423px'
 * } );
 * ```
 *
 * It uses the [native DOM resize observer](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver)
 * under the hood.
 */
var ResizeObserver = /*#__PURE__*/function () {
  /**
   * Creates an instance of the `ResizeObserver` class.
   *
   * @param element A DOM element that is to be observed for resizing. Note that
   * the element must be visible (i.e. not detached from DOM) for the observer to work.
   * @param callback A function called when the observed element was resized. It passes
   * the [`ResizeObserverEntry`](https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry)
   * object with information about the resize event.
   */
  function ResizeObserver(element, callback) {
    _classCallCheck(this, ResizeObserver);
    // **Note**: For the maximum performance, this class ensures only a single instance of the native
    // observer is used no matter how many instances of this class were created.
    if (!ResizeObserver._observerInstance) {
      ResizeObserver._createObserver();
    }
    this._element = element;
    this._callback = callback;
    ResizeObserver._addElementCallback(element, callback);
    ResizeObserver._observerInstance.observe(element);
  }
  /**
   * The element observed by this observer.
   */
  _createClass(ResizeObserver, [{
    key: "element",
    get: function get() {
      return this._element;
    }
    /**
     * Destroys the observer which disables the `callback` passed to the {@link #constructor}.
     */
  }, {
    key: "destroy",
    value: function destroy() {
      ResizeObserver._deleteElementCallback(this._element, this._callback);
    }
    /**
     * Registers a new resize callback for the DOM element.
     */
  }], [{
    key: "_addElementCallback",
    value: function _addElementCallback(element, callback) {
      if (!ResizeObserver._elementCallbacks) {
        ResizeObserver._elementCallbacks = new Map();
      }
      var callbacks = ResizeObserver._elementCallbacks.get(element);
      if (!callbacks) {
        callbacks = new Set();
        ResizeObserver._elementCallbacks.set(element, callbacks);
      }
      callbacks.add(callback);
    }
    /**
     * Removes a resize callback from the DOM element. If no callbacks are left
     * for the element, it removes the element from the native observer.
     */
  }, {
    key: "_deleteElementCallback",
    value: function _deleteElementCallback(element, callback) {
      var callbacks = ResizeObserver._getElementCallbacks(element);
      // Remove the element callback. Check if exist first in case someone
      // called destroy() twice.
      if (callbacks) {
        callbacks.delete(callback);
        // If no callbacks left for the element, also remove the element.
        if (!callbacks.size) {
          ResizeObserver._elementCallbacks.delete(element);
          ResizeObserver._observerInstance.unobserve(element);
        }
      }
      if (ResizeObserver._elementCallbacks && !ResizeObserver._elementCallbacks.size) {
        ResizeObserver._observerInstance = null;
        ResizeObserver._elementCallbacks = null;
      }
    }
    /**
     * Returns are registered resize callbacks for the DOM element.
     */
  }, {
    key: "_getElementCallbacks",
    value: function _getElementCallbacks(element) {
      if (!ResizeObserver._elementCallbacks) {
        return null;
      }
      return ResizeObserver._elementCallbacks.get(element);
    }
    /**
     * Creates the single native observer shared across all `ResizeObserver` instances.
     */
  }, {
    key: "_createObserver",
    value: function _createObserver() {
      ResizeObserver._observerInstance = new global.window.ResizeObserver(function (entries) {
        var _iterator = _createForOfIteratorHelper(entries),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var entry = _step.value;
            var callbacks = ResizeObserver._getElementCallbacks(entry.target);
            if (callbacks) {
              var _iterator2 = _createForOfIteratorHelper(callbacks),
                _step2;
              try {
                for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
                  var callback = _step2.value;
                  callback(entry);
                }
              } catch (err) {
                _iterator2.e(err);
              } finally {
                _iterator2.f();
              }
            }
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      });
    }
  }]);
  return ResizeObserver;
}();
/**
 * The single native observer instance shared across all {@link module:utils/dom/resizeobserver~ResizeObserver} instances.
 */
ResizeObserver._observerInstance = null;
/**
 * A mapping of native DOM elements and their callbacks shared across all
 * {@link module:utils/dom/resizeobserver~ResizeObserver} instances.
 */
ResizeObserver._elementCallbacks = null;
export default ResizeObserver;